import { DEFAULT_DISCOUNT_POLICY } from '@zuruflex/core/pricing';

/**
 * Copy for the standalone marketing pages.
 *
 * Held here rather than in `@zuruflex/i18n` for the same reason the policy
 * pages are — see `@zuruflex/core/legal`. The difference is that this copy
 * *should* be translated before Kigali sees it, and the move is mechanical
 * when somebody has the French and Kinyarwanda: lift each string into
 * `web.json` under a key named for the page. It is here now so that eight
 * new pages do not land as several hundred lines of machine-translated
 * marketing nobody in the building can check.
 */

export interface Point {
  title: string;
  body: string;
}

/* ── list your car ─────────────────────────────────────────────────── */

export const LIST_YOUR_CAR = {
  title: 'Put your car to work.',
  lead: 'Zuruflex finds the renters, handles the chat, takes the payment and hands you the keys back. You own the car; we run the rental.',
  /** Three reasons an owner reads past the headline. */
  why: [
    {
      title: 'We do the talking',
      body: 'Every enquiry, every negotiation and every reschedule happens in our WhatsApp, not your phone. You see confirmed bookings, not tyre-kickers.',
    },
    {
      title: 'You set the terms',
      body: 'Your daily rate, your calendar, your pick-up point. Block the dates you want the car yourself and it simply stops being offered.',
    },
    {
      title: 'Paid without chasing',
      body: 'Renters pay Zuruflex up front by card or mobile money. Payouts land in your bank account or wallet on a schedule, with the deposit already settled.',
    },
  ] satisfies Point[],
  /** What actually happens, in the order it happens. */
  steps: [
    {
      title: 'Tell us about the car',
      body: 'Make, model, year, where it lives and what you want per day. Ten minutes in the partner portal.',
    },
    {
      title: 'Send your paperwork',
      body: 'Registration, insurance and roadworthiness, plus ID for you or your business. We check them before anything goes live.',
    },
    {
      title: 'We photograph and list it',
      body: 'Good photographs are most of why one car gets booked and another does not, so we would rather shoot it than have you guess.',
    },
    {
      title: 'Take your first booking',
      body: 'You get the request, you accept it, the renter pays, and you hand over the keys.',
    },
  ] satisfies Point[],
  /** The questions that decide whether somebody signs up. */
  faq: [
    {
      title: 'What does it cost?',
      body: 'Nothing to list. Zuruflex takes a commission on each completed rental — the rate is set out in the partner terms before you list anything.',
    },
    {
      title: 'Who is liable if the car is damaged?',
      body: 'The car carries its own insurance and every renter is held to an excess. The insurance page sets out what is covered; the partner terms set out how a claim runs.',
    },
    {
      title: 'Do I have to provide a driver?',
      body: 'Only if you want to. Cars can be listed self-drive, with a driver, or either — and the ones with a driver earn a separate daily rate on top.',
    },
    {
      title: 'Can I still use my own car?',
      body: 'Yes. Block the dates in the portal and it is not offered to anybody.',
    },
    {
      title: 'How many cars can I list?',
      body: 'One or a hundred. Most of the fleet belongs to operators running several.',
    },
  ] satisfies Point[],
};

/* ── with a driver ─────────────────────────────────────────────────── */

export const WITH_DRIVER = {
  title: 'Or let somebody else drive.',
  lead: 'Every Zuruflex car can be hired with one of our partners’ drivers. Same booking, same chat, one extra line on the price.',
  why: [
    {
      title: 'They know the city',
      body: 'Accra and Kigali traffic is a skill. A driver who works these roads daily will get you there faster than a map will.',
    },
    {
      title: 'One price, agreed up front',
      body: 'The driver is a separate daily rate quoted with the car, not a tip negotiated at the end of the day.',
    },
    {
      title: 'Nothing to arrange',
      body: 'No second booking, no separate company. Ask for a driver in the same message you ask for the car.',
    },
  ] satisfies Point[],
  /** Who actually books this, which is how somebody recognises themselves. */
  who: [
    {
      title: 'Visiting on business',
      body: 'Meetings across town, nowhere to park, and no appetite for a licence check on landing.',
    },
    {
      title: 'Events and airport runs',
      body: 'A wedding, a conference, a family arriving at 2am. Somebody waiting at the kerb.',
    },
    {
      title: 'Long trips upcountry',
      body: 'Kumasi, Cape Coast, Musanze. Roads worth having somebody else drive.',
    },
  ] satisfies Point[],
  faq: [
    {
      title: 'How much is the driver?',
      body: 'A daily rate set by the car’s owner, shown alongside the car’s own rate before you pay. Fuel is handled the same way as a self-drive hire.',
    },
    {
      title: 'Can every car be hired with a driver?',
      body: 'No. Some are self-drive only and some are driver-only — the car’s page says which, and the fleet can be filtered down to the ones that come with a driver.',
    },
    {
      title: 'Do I still need my licence?',
      body: 'Not for a driver-only hire. For a car you might also drive yourself, bring it.',
    },
    {
      title: 'How long are the drivers booked for?',
      body: 'By the day, alongside the car. Tell us the hours you need in the chat and we will tell you what it costs.',
    },
  ] satisfies Point[],
};

/* ── long-term and business ────────────────────────────────────────── */

const { weeklyRate, monthlyRate, weeklyFromDays, monthlyFromDays } = DEFAULT_DISCOUNT_POLICY;

/**
 * The published discount tiers, read out of the pricing engine rather than
 * typed in beside it — the same reasoning as the cancellation table in
 * `@zuruflex/core/legal`. `DEFAULT_DISCOUNT_POLICY` is what quotes a long hire,
 * so quoting different numbers here would be quoting a price we do not
 * honour. Operators can tune the policy per country, which is why the page
 * says "at least".
 */
export const LONG_TERM = {
  title: 'By the week, by the month.',
  lead: 'A long hire is cheaper per day than a weekend, and the discount is applied to the quote automatically — there is nothing to ask for.',
  tiers: [
    {
      title: `${weeklyFromDays} days or more`,
      body: `At least ${Math.round(weeklyRate * 100)}% off the daily rate, applied to every day of the hire.`,
    },
    {
      title: `${monthlyFromDays} days or more`,
      body: `At least ${Math.round(monthlyRate * 100)}% off the daily rate. This is the rate most business hires land on.`,
    },
  ] satisfies Point[],
  why: [
    {
      title: 'No fleet to own',
      body: 'A car for a project, a secondment or a season, without buying it, insuring it or selling it afterwards.',
    },
    {
      title: 'Swap it when it stops fitting',
      body: 'Three months in a saloon and then a month needing a 4x4 is a conversation, not a new contract.',
    },
    {
      title: 'One invoice',
      body: 'Monthly billing for companies, with the paperwork your finance team will ask for.',
    },
  ] satisfies Point[],
  faq: [
    {
      title: 'Is the discount automatic?',
      body: `Yes. Any hire of ${weeklyFromDays} days or more is quoted with it already applied. You do not need to ask, and you cannot miss it.`,
    },
    {
      title: 'Can I pay monthly rather than up front?',
      body: 'For longer hires and for companies, usually — ask and we will set it up. Shorter hires are paid up front.',
    },
    {
      title: 'Who services the car?',
      body: 'We do. A long hire includes scheduled servicing, and we arrange a replacement while it is in the workshop.',
    },
    {
      title: 'Is there a maximum?',
      body: 'No. Tell us how long you need it for.',
    },
  ] satisfies Point[],
};

/* ── about ─────────────────────────────────────────────────────────── */

export const ABOUT = {
  title: 'Renting a car should take one message.',
  lead: 'Zuruflex is a car rental company in Accra and Kigali built around the app people already have open.',
  body: [
    'Renting a car in most of West and East Africa means a branch visit, a stack of forms, a deposit nobody explains and an afternoon gone. The cars are fine. The process is the problem.',
    'So Zuruflex put the whole thing in WhatsApp. Tell us the dates, we tell you what is free and what it costs, the owner confirms, you pay by mobile money, and you collect the keys. No app to install, no account to create, no queue.',
    'Behind that conversation is a real fleet: cars Zuruflex owns and cars belonging to partner operators we verify, photograph and list ourselves. Every one of them is insured, serviced and checked before it goes out.',
  ],
  facts: [
    {
      title: 'Where we operate',
      body: 'Accra, Ghana and Kigali, Rwanda, with delivery across both cities.',
    },
    { title: 'How you book', body: 'WhatsApp, in about three minutes, at any hour.' },
    {
      title: 'How you pay',
      body: 'Mobile money in the chat, or card and mobile money on the website — always after the owner confirms your dates.',
    },
    {
      title: 'Who owns the cars',
      body: 'Zuruflex and verified partner operators — the listing says which.',
    },
  ] satisfies Point[],
};

/* ── find your booking ─────────────────────────────────────────────── */

export const FIND_BOOKING = {
  title: 'Find your booking.',
  lead: 'Your booking lives in the WhatsApp conversation you made it in. Here is how to get back to it.',
  steps: [
    {
      title: 'Open WhatsApp and search for Zuruflex',
      body: 'Every booking you have made is in that one thread, with the code we sent you when it was confirmed.',
    },
    {
      title: 'Look for the code',
      body: 'It looks like ZF-8K2QD. Anyone at Zuruflex can pull up your booking from it.',
    },
    {
      title: 'Ask us anything about it',
      body: 'Change the dates, add a driver, extend the hire, cancel it, or ask where to collect. Same thread, same people.',
    },
  ] satisfies Point[],
};
